import Link from "next/link";
import Image from "next/image";
import { CategoryEntityResponseCollection } from "gql-types";
import { useQuery } from "@apollo/client";
import { categoriesGql } from "gql/queries";
import { SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "next-i18next";
import { categoryTranslateKey } from "../../helpers/constants/translate";
import { useRouter } from "next/router";
import { SIGNIN_URL, SIGNUP_URL } from "helpers/constants/routes";
import { useGlobalState } from "context/GlobalStateContext";
import { FaFacebook, FaInstagram, FaTiktok } from "react-icons/fa";
import { useWindowSize } from "helpers/scroll/animateScroll";

const Footer = (props) => {
  const { t: translate } = useTranslation("layout");
  const { t: categoriesTranslate } = useTranslation("categories");
  const { isLoggedIn } = useGlobalState();

  const { loading, data } = useQuery<{ categories?: CategoryEntityResponseCollection }>(
    categoriesGql,
  );
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    if (!loading) {
      if (data) {
        setCategories(data?.categories?.data as SetStateAction<never[]>);
      }
    }
  }, [loading]);

  const { width } = useWindowSize();

  const isTablet = width < 950;
  const isMobile = width < 547;
  const navbar = [
    { route: "/", msgKey: "accueil" },
    { route: "/faq", msgKey: "faq" },
    { route: "/about", msgKey: "about_us" },
    { route: "/#contact", msgKey: "contact" },
    { route: "/categories", msgKey: "categories" },
    { route: "/blog", msgKey: "blog" },
    { route: "/offers", msgKey: "offers" },
  ];
  const router = useRouter();

  const navigateTo = (path: string) => {
    const currentUrl = router.asPath;
    const { next_url } = router.query as { next_url?: string };
    router
      .push({
        pathname: path,
        query: { next_url: currentUrl.startsWith("/auth/") ? next_url : currentUrl },
      })
      .then();
  };
  const currentYear = new Date()?.getFullYear();
  return (
    <>
      <footer className="bg-blue p-4 md:p-8 text-white xl:flex flex-col xl:justify-between relative">
        <button
          className="md:flex absolute top-0 md:-top-6  max-md:left-[50%]   max-md:translate-y-[-50%]    max-md:translate-x-[-50%]   ltr:md:right-4 rtl:md:left-4"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        >
          <Image src="/images/topArrow.svg" width={50} height={50} alt="arrowScrollTopIcon" />
        </button>
        <div className="flex md:flex-row flex-col sm:items-start justify-around xl:grow md:pt-20 pt-5   ">
          <div className={`flex md:self-start flex-col items-center md:items-start sm:mt-10 `}>
            <Image
              src="/images/footer_logo.svg"
              alt="yawakit logo"
              height={160}
              width={190}
              className="lg:w-[100%] md:w-[80%]  mb-4"
            />

            <div className="flex gap-2 mr-4 mt-3">
              <Link
                href="https://www.facebook.com/profile.php?id=61570309597330"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaFacebook
                  size={40}
                  style={{
                    color: "white",
                    strokeWidth: 0.8,
                    padding: 8,
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    borderRadius: "50%",
                  }}
                  className="zoom-icon"
                />
              </Link>
              <Link
                href="https://www.instagram.com/yawakit_/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaInstagram
                  size={40}
                  style={{
                    color: "white",
                    strokeWidth: 0.8,
                    padding: 8,
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    borderRadius: "50%",
                  }}
                  className="zoom-icon"
                />
              </Link>
              <Link href="https://www.tiktok.com/@yawakit_?_t=ZM-8u2io4q63PD&_r=1" target="_blank">
                <FaTiktok
                  size={40}
                  style={{
                    color: "white",
                    strokeWidth: 0.8,
                    padding: 10,
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    borderRadius: "50%",
                  }}
                  className="zoom-icon"
                />
              </Link>
            </div>
          </div>
          <div className="flex md:self-start flex-col items-center md:items-start">
            <div className="font-bold my-4 mt-8 text-xl lg:text-2xl ">{translate("menu")}</div>
            <div
              className={`grid ${
                isMobile
                  ? "grid-cols-2" // ≤ 547px → 2 colonnes
                  : isTablet
                    ? "grid-cols-1" // entre 547px et 950px → 1 colonne
                    : "grid-cols-2" // > 950px → 2 colonnes
              } gap-4 md:gap-4 justify-items-start items-center justify-between min-w-fit`}
            >
              {navbar?.map((item, index) => (
                <span key={index} className={`font-light py-1 `}>
                  <Link href={item?.route} passHref className="font-light py-2">
                    {translate(item?.msgKey)}
                  </Link>
                </span>
              ))}
            </div>
          </div>

          <div className="flex  md:self-start flex-col items-center md:items-start sm:ps-0 ltr:ps-0 rtl:pe-0 lg-ps-0">
            <div className="font-bold my-4 mt-8 text-xl lg:text-2xl">{translate("nav_link3")}</div>

            <div
              className={`grid ${
                isMobile
                  ? "grid-cols-2" // ≤ 547px → 2 cols
                  : isTablet
                    ? "grid-cols-1" // 547px-950px → 1 col
                    : "grid-cols-2 lg:grid-cols-3" // > 950px → 2 cols, and 3 cols on lg
              } justify-items-start items-center justify-between min-w-fit`}
            >
              {categories?.map((item: any) => (
                <span
                  key={item.attributes.name}
                  className="font-light pr-3  py-1 md:py-3 hover:cursor-pointer whitespace-nowrap"
                >
                  <Link
                    href={`/categories/${item.attributes.slug}`}
                    passHref
                    key={`footer_category__details__key__${item.attributes.slug}`}
                  >
                    {categoriesTranslate(categoryTranslateKey(item.attributes.slug))}
                  </Link>
                </span>
              ))}
            </div>
          </div>
          <div className="flex md:self-start flex-col items-center md:items-start gap-3 md:ps-10 lg:ps-0">
            <div className="font-bold my-4 mt-8 text-xl lg:text-2xl">{translate("contact")}</div>
            <div className="flex  font-light p-0 md:my-2 md:py-2 md:pt-0">
              <Link href="mailto:contact@yawakit.com">contact@yawakit.com</Link>
            </div>
            <div className="flex xl:ml-0 font-light p-0 md:my-2 md:py-2 md:pt-0 ">
              <Link href="tel:+21624804545" dir="ltr">
                {translate("phone_number")}
              </Link>
            </div>
            <div className="flex xl:ml-0 font-light p-0 md:my-2 md:py-2 md:pt-0">
              <Link href="/request-demo"> {translate("request_demo")}</Link>
            </div>
            <div className="flex flex-col items-center xl:flex-col md:flex-row md:justify-evenly lg:justify-around justify-around mx-auto max-w-[100%]">
              {/* <div
                className="flex flex-col xl:flex-col xl:space-x-0 
                  lg:flex-row lg:space-x-16 lg:justify-between justify-start relative  m-4 rtl:lg:gap-8 ltr:-left-8 lg:ltr:-left-4 rtl:-right-8 lg:rtl:right-0"
              ></div> */}
              {!isLoggedIn && (
                <div className="shadow-md rtl:lg:ml-20 ltr:lg:mr-14">
                  <button
                    id="login"
                    className={`${isTablet ? "w-[150px]" : "w-[200px]"} text-white font-semibold bg-[#0F1A4B]  h-[45px] rounded-md m-auto block mb-4`}
                    onClick={() => navigateTo(SIGNIN_URL)}
                  >
                    {translate("signin")}
                  </button>
                  <button
                    id="signup"
                    className={`${isTablet ? "w-[150px]" : "w-[200px]"} bg-white font-semibold text-[#0F1A4B] h-[45px] rounded-md m-auto block`}
                    onClick={() => navigateTo(SIGNUP_URL)}
                  >
                    {translate("signup")}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex m-2 justify-end mt-20">
          <Link href="/privacy">{translate("policy")}</Link>
        </div>
      </footer>
      <div className="flex justify-between">
        <div className="bg-yellow w-1/4 h-3"></div>
        <p className="m-1 text-xs text-center" dir="ltr">
          Powered by INSTACODIN {currentYear} &copy; | All Rights Reserved.
        </p>
        <div className="bg-yellow w-1/4 h-3"></div>
      </div>
    </>
  );
};

export default Footer;
